import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';

// ==================== INTERFACES ====================

export interface CalendarEvent {
  id: number;
  user_id: string;
  case_id?: number;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  location?: string;
  type: string;
  color: string;
  all_day: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CalendarEventCreateInput {
  user_id: string;
  case_id?: number;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  location?: string;
  type: string;
  color: string;
  all_day: boolean;
}

export interface CalendarEventUpdateInput extends Partial<CalendarEventCreateInput> {
  id: number;
}

// ==================== INITIAL STATE ====================

interface CalendarState {
  events: CalendarEvent[];
  loading: boolean;
  error: string | null;
}

const initialState: CalendarState = {
  events: [],
  loading: false,
  error: null,
};

// ==================== ASYNC THUNKS ====================

export const fetchEvents = createAsyncThunk(
  'calendar/fetchEvents',
  async (params: string | { userId: string; month?: number; year?: number }) => {
    // Accepts a bare userId (defaults to the backend's current month/year)
    // for backward compatibility, or { userId, month, year } so browsing
    // to a different month actually fetches that month's data instead of
    // re-filtering whatever the initial month's payload happened to be.
    const normalized = typeof params === 'string' ? { userId: params } : params;
    const response = await api.get(`/calendar/events/${normalized.userId}`, {
      params: { month: normalized.month, year: normalized.year },
    });
    return response.data;
  }
);

export const createEvent = createAsyncThunk(
  'calendar/createEvent',
  async (eventData: CalendarEventCreateInput) => {
    const response = await api.post('/calendar/event', eventData);
    return response.data;
  }
);

export const updateEvent = createAsyncThunk(
  'calendar/updateEvent',
  async (eventData: CalendarEventUpdateInput) => {
    const response = await api.put(`/calendar/event/${eventData.id}`, eventData);
    return response.data;
  }
);

export const deleteEvent = createAsyncThunk(
  'calendar/deleteEvent',
  async ({ eventId, userId }: { eventId: number; userId: string }) => {
    await api.delete(`/calendar/event/${eventId}/${userId}`);
    return eventId;
  }
);

// ==================== SLICE ====================

const calendarSlice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Events
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload.data || action.payload;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch events';
      })
      
      // Create Event
      .addCase(createEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.events.push(action.payload.data || action.payload);
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create event';
      })
      
      // Update Event
      .addCase(updateEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.events.findIndex(event => event.id === action.payload.data?.id || action.payload.id);
        if (index !== -1) {
          state.events[index] = action.payload.data || action.payload;
        }
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update event';
      })
      
      // Delete Event
      .addCase(deleteEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.events = state.events.filter(event => event.id !== action.payload);
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete event';
      });
  },
});

export default calendarSlice.reducer;
